import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Paper,
  Grid,
} from "@mui/material";
import { Layers, Pencil, Check, X } from "lucide-react";
import {
  getStatusUI,
  capitalize,
  LOCKED_GRADE_STATUSES,
  GRADE_STATUS_GROUPS,
} from "../../../config/settings.config";
import PageTitle from "../../../components/PageTitle";

const FIRST_BATCH_NUMBERS = [1, 2, 4, 5];

export default function MultiSelector({
  title = "Items",
  icon: Icon = Layers,
  selected,
  setSelected,
}) {
  const [editing, setEditing] = useState(false);
  const [tempSelected, setTempSelected] = useState(selected);

  const toggle = (item) => {
    setTempSelected((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const cancel = () => {
    setTempSelected(selected);
    setEditing(false);
  };

  const save = () => {
    setSelected(tempSelected);
    setEditing(false);
  };

  return (
    <Paper elevation={0}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={4}
      >
        <PageTitle parts={["Settings", "Leads", "Grade Status"]} />
        {!editing ? (
          <IconButton onClick={() => setEditing(true)}>
            <Pencil size={16} />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={1}>
            <IconButton onClick={save}>
              <Check size={18} />
            </IconButton>
            <IconButton onClick={cancel}>
              <X size={18} />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Grid container spacing={4}>
        {Object.entries(GRADE_STATUS_GROUPS).map(
          ([group, items], groupIndex) => (
            <Grid size={{ xs: 12, md: 6 }} key={group}>
              <Typography
                sx={{
                  mb: 1.5,
                  fontSize: 13,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  color: "text.secondary",
                }}
              >
                {group}
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={2}>
                {items.map((item, index) => {
                  const active = tempSelected.includes(item);
                  const locked = LOCKED_GRADE_STATUSES.includes(item);
                  const ui = getStatusUI(item);
                  const isFirstGroup = groupIndex === 0;
                  const batchNumber = FIRST_BATCH_NUMBERS[index];

                  return (
                    <Chip
                      key={item}
                      clickable={editing && !locked}
                      onClick={() => editing && !locked && toggle(item)}
                      icon={ui.icon}
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 14,
                            }}
                          >
                            {capitalize(item)}
                          </Typography>

                          {isFirstGroup && batchNumber && (
                            <Box
                              sx={{
                                ml: 1,
                                px: 1.2,
                                height: 20,
                                borderRadius: 1.5,
                                bgcolor: active
                                  ? "rgba(255,255,255,0.85)"
                                  : "#E5E7EB",
                                color: "#111827",
                                fontSize: 12,
                                fontWeight: 800,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {batchNumber}
                            </Box>
                          )}
                        </Box>
                      }
                      sx={{
                        px: 1.5,
                        py: 2,
                        borderRadius: 3,
                        bgcolor: active ? ui.color : "#F3F4F6",
                        color: active ? "white" : "#111827",
                        opacity: locked && !active ? 0.6 : 1,
                        cursor: locked
                          ? "not-allowed"
                          : editing
                          ? "pointer"
                          : "default",
                        "& .MuiChip-icon": {
                          color: active ? "white" : ui.color,
                        },
                        ...(editing && !locked
                          ? {
                              "&:hover": {
                                bgcolor: active
                                  ? ui.color
                                  : "rgba(79,70,229,0.12)",
                                boxShadow: active
                                  ? "0 0 0 2px rgba(255,255,255,0.4)"
                                  : "0 0 0 2px rgba(79,70,229,0.25)",
                                transform: "translateY(-1px)",
                                transition: "all .18s ease",
                              },
                            }
                          : {}),
                      }}
                    />
                  );
                })}
              </Stack>
            </Grid>
          )
        )}
      </Grid>
    </Paper>
  );
}
